import { useCallback, useEffect, useRef, useState } from 'react';
import { Flag, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { getProfileFlags, updateProfileFlag } from '@/lib/api';

export default function ProfileFlagControl({ kind, recordId }) {
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const request = useRef(0);
  const savingRef = useRef(false);
  const channel = useRef(null);
  const reload = useCallback(async () => {
    if (!getProfileFlags || savingRef.current) return;
    const version = ++request.current;
    try {
      const { data } = await getProfileFlags(kind, recordId);
      if (version === request.current) { setState(data); setFailed(false); }
    } catch {
      if (version === request.current) setFailed(true);
    }
  }, [kind, recordId]);
  useEffect(() => {
    setState(null); setFailed(false);
    reload();
    window.addEventListener('focus', reload);
    window.addEventListener('profile-flags-changed', reload);
    if (typeof BroadcastChannel !== 'undefined') {
      channel.current = new BroadcastChannel('profile-flags');
      channel.current.onmessage = reload;
    }
    return () => {
      request.current += 1;
      window.removeEventListener('focus', reload);
      window.removeEventListener('profile-flags-changed', reload);
      channel.current?.close(); channel.current = null;
    };
  }, [reload]);
  const toggle = async () => {
    if (!state || savingRef.current) return;
    savingRef.current = true; setSaving(true); request.current += 1;
    try {
      const { data } = await updateProfileFlag(kind, recordId, { active: !state.flagged, revision: state.own_revision });
      setState(data); setFailed(false);
      toast.success(data.flagged ? 'Profile flagged' : 'Flag removed');
      channel.current?.postMessage('changed');
      window.dispatchEvent(new Event('profile-flags-changed'));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update the flag. Please try again.');
      savingRef.current = false;
      await reload();
    } finally { savingRef.current = false; setSaving(false); }
  };
  // Keeps the shared header usable in environments without the flag API.
  if (!getProfileFlags) return null;
  const active = state?.flagged === true;
  const label = saving ? 'Saving flag' : !state ? 'Loading flag' : active ? 'Remove flag' : 'Flag profile';
  return <div data-testid="profile-flag-control">
    {failed ? <Button type="button" variant="outline" size="icon" aria-label="Flags unavailable. Retry" title="Flags unavailable. Retry" className="bg-white text-red-700" onClick={reload}><RotateCcw aria-hidden="true" /></Button>
      : <Button type="button" variant="outline" size="icon" aria-pressed={active} aria-label={label} title={label}
        className={active ? 'border-amber-300 bg-amber-100 text-amber-950 hover:bg-amber-200' : 'border-white/40 bg-white text-[#062B1A] hover:bg-amber-50'} disabled={!state || saving} onClick={toggle}>
        {saving || !state ? <Loader2 aria-hidden="true" className="animate-spin" /> : <Flag aria-hidden="true" className={active ? 'fill-current' : ''} />}
      </Button>}
  </div>;
}
